import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Facebook, Twitter, Instagram, Youtube, Linkedin, Send, MessageCircle } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';

const Footer = () => {
  const [settings, setSettings] = useState(null);
  const { theme } = useTheme();
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/settings/`)
      .then(res => res.json())
      .then(data => setSettings(data))
      .catch(err => console.error('Error fetching settings:', err));
  }, [BACKEND_URL]);

  const contactSettings = settings?.contact_settings || {};

  const cleanTel = (val) => String(val || '').replace(/[^\d+]/g, '');

  const getWhatsappUrl = (val) => {
    if (!val) return '#';
    if (val.startsWith('http://') || val.startsWith('https://')) return val;
    const cleanNum = String(val).replace(/[^\d]/g, '');
    return `https://wa.me/${cleanNum.startsWith('967') ? cleanNum : '967' + cleanNum.replace(/^0+/, '')}`;
  };

  const getWhatsappDisplay = (val) => {
    if (!val) return '';
    if (val.includes('wa.me/')) {
      const num = val.split('wa.me/')[1].replace(/[^\d+]/g, '');
      return num.startsWith('+') ? num : '+' + num;
    }
    return val;
  };

  const getMapsUrl = () => {
    return contactSettings.map_url || contactSettings.map_link || 'https://maps.app.goo.gl/M8dLpS6wdmQdVHB49';
  };

  return (
    <footer className={`text-white transition-colors duration-300 ${
      theme === 'dark' 
        ? 'bg-[#050D1A]' 
        : 'bg-[#0F388E]'
    }`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* About Section */}
          <div>
            <div className="flex items-center gap-3 mb-4">
              {settings?.site_logo ? (
                <img 
                  src={settings.site_logo} 
                  alt="شعار الجامعة" 
                  className="h-16 w-auto object-contain"
                />
              ) : (
                <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center">
                  <span className="text-[#C9A040] font-bold text-xl">ج</span>
                </div>
              )}
            </div>
            <p className="text-sm text-white/80 leading-relaxed">
              {contactSettings.footer_about_ar || 'أول جامعة أهلية متخصصة في العلوم الطبية في اليمن، نلتزم بتقديم تعليم طبي متميز يؤهل كوادر طبية محترفة.'}
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="font-bold text-lg mb-4 text-[#C9A040]">روابط سريعة</h3>
            <ul className="space-y-2">
              <li>
                <Link to="/about" onClick={() => window.scrollTo({ top: 0, left: 0, behavior: 'smooth' })} className="text-sm text-white/80 hover:text-[#C9A040] transition-colors">
                  عن الجامعة
                </Link>
              </li>
              <li>
                <Link to="/colleges" onClick={() => window.scrollTo({ top: 0, left: 0, behavior: 'smooth' })} className="text-sm text-white/80 hover:text-[#C9A040] transition-colors">
                  الكليات
                </Link>
              </li>
              <li>
                <Link to="/departments" onClick={() => window.scrollTo({ top: 0, left: 0, behavior: 'smooth' })} className="text-sm text-white/80 hover:text-[#C9A040] transition-colors">
                  الإدارات والمراكز
                </Link>
              </li>
              <li>
                <Link to="/programs" onClick={() => window.scrollTo({ top: 0, left: 0, behavior: 'smooth' })} className="text-sm text-white/80 hover:text-[#C9A040] transition-colors">
                  البرامج الأكاديمية
                </Link>
              </li>
              <li>
                <Link to="/faculty" onClick={() => window.scrollTo({ top: 0, left: 0, behavior: 'smooth' })} className="text-sm text-white/80 hover:text-[#C9A040] transition-colors">
                  أعضاء هيئة التدريس
                </Link>
              </li>
              <li>
                <Link to="/news" onClick={() => window.scrollTo({ top: 0, left: 0, behavior: 'smooth' })} className="text-sm text-white/80 hover:text-[#C9A040] transition-colors">
                  الأخبار والفعاليات
                </Link>
              </li>
              <li>
                <Link to="/research" onClick={() => window.scrollTo({ top: 0, left: 0, behavior: 'smooth' })} className="text-sm text-white/80 hover:text-[#C9A040] transition-colors">
                  الأبحاث المنشورة
                </Link>
              </li>
              <li>
                <Link to="/gallery" onClick={() => window.scrollTo({ top: 0, left: 0, behavior: 'smooth' })} className="text-sm text-white/80 hover:text-[#C9A040] transition-colors">
                  معرض الصور
                </Link>
              </li>
              <li>
                <Link to="/register" onClick={() => window.scrollTo({ top: 0, left: 0, behavior: 'smooth' })} className="text-sm text-white/80 hover:text-[#C9A040] transition-colors">
                  التسجيل الإلكتروني
                </Link>
              </li>
            </ul>
          </div>

          {/* Custom Links Section */}
          <div>
            <h3 className="font-bold text-lg mb-4 text-[#C9A040]">روابط مهمة</h3>
            <ul className="space-y-2">
              {contactSettings.custom_links && contactSettings.custom_links.length > 0 ? (
                contactSettings.custom_links.map((link, index) => {
                  let targetUrl = link.url || '/';
                  if (targetUrl.includes('preview.emergentagent.com') || targetUrl.includes('alnafisuni.edu.ye')) {
                    try {
                      const parsed = new URL(targetUrl);
                      targetUrl = parsed.pathname || '/';
                    } catch (e) {
                      targetUrl = '/';
                    }
                  }
                  const isInternal = targetUrl.startsWith('/') || !targetUrl.startsWith('http');
                  const finalPath = isInternal && !targetUrl.startsWith('/') ? `/${targetUrl}` : targetUrl;

                  return (
                    <li key={index}>
                      {isInternal ? (
                        <Link 
                          to={finalPath}
                          onClick={() => window.scrollTo({ top: 0, left: 0, behavior: 'smooth' })}
                          className="text-sm text-white/80 hover:text-[#C9A040] transition-colors block"
                        >
                          {link.name_ar}
                        </Link>
                      ) : (
                        <a 
                          href={finalPath}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-white/80 hover:text-[#C9A040] transition-colors block"
                        >
                          {link.name_ar}
                        </a>
                      )}
                    </li>
                  );
                })
              ) : (
                <>
                  <li>
                    <Link to="/register" onClick={() => window.scrollTo({ top: 0, left: 0, behavior: 'smooth' })} className="text-sm text-white/80 hover:text-[#C9A040] transition-colors">
                      التسجيل الإلكتروني
                    </Link>
                  </li>
                  <li>
                    <Link to="/programs" onClick={() => window.scrollTo({ top: 0, left: 0, behavior: 'smooth' })} className="text-sm text-white/80 hover:text-[#C9A040] transition-colors">
                      البرامج الأكاديمية
                    </Link>
                  </li>
                  <li>
                    <Link to="/departments" onClick={() => window.scrollTo({ top: 0, left: 0, behavior: 'smooth' })} className="text-sm text-white/80 hover:text-[#C9A040] transition-colors">
                      المراكز والوحدات التخصصية
                    </Link>
                  </li>
                  <li>
                    <Link to="/news" onClick={() => window.scrollTo({ top: 0, left: 0, behavior: 'smooth' })} className="text-sm text-white/80 hover:text-[#C9A040] transition-colors">
                      الأخبار والفعاليات
                    </Link>
                  </li>
                  <li>
                    <Link to="/contact" onClick={() => window.scrollTo({ top: 0, left: 0, behavior: 'smooth' })} className="text-sm text-white/80 hover:text-[#C9A040] transition-colors">
                      تواصل معنا
                    </Link>
                  </li>
                </>
              )}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="font-bold text-lg mb-4 text-[#C9A040]">تواصل معنا</h3>
            <ul className="space-y-3">
              {contactSettings.address_ar && (
                <li className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-[#C9A040] flex-shrink-0 mt-0.5" />
                  <a 
                    href={getMapsUrl()}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-white/80 hover:text-[#C9A040] hover:underline transition-colors leading-relaxed"
                    title="فتح موقع الجامعة على خرائط Google"
                  >
                    {contactSettings.address_full_ar || contactSettings.address_ar}
                  </a>
                </li>
              )}
              {contactSettings.phone && (
                <li className="flex items-center gap-3">
                  <Phone className="w-5 h-5 text-[#C9A040] flex-shrink-0" />
                  <a 
                    href={`tel:${cleanTel(contactSettings.phone)}`}
                    className="text-sm text-white/80 hover:text-[#C9A040] hover:underline transition-colors"
                    dir="ltr"
                    title="اتصال مباشر"
                  >
                    {contactSettings.phone}
                  </a>
                </li>
              )}
              {contactSettings.phone_secondary && contactSettings.phone_secondary !== contactSettings.phone && (
                <li className="flex items-center gap-3">
                  <Phone className="w-5 h-5 text-[#C9A040] flex-shrink-0" />
                  <a 
                    href={`tel:${cleanTel(contactSettings.phone_secondary)}`}
                    className="text-sm text-white/80 hover:text-[#C9A040] hover:underline transition-colors"
                    dir="ltr"
                    title="اتصال مباشر"
                  >
                    {contactSettings.phone_secondary}
                  </a>
                </li>
              )}
              {contactSettings.mobile && contactSettings.mobile !== contactSettings.phone && contactSettings.mobile !== contactSettings.phone_secondary && (
                <li className="flex items-center gap-3">
                  <Phone className="w-5 h-5 text-[#C9A040] flex-shrink-0" />
                  <a 
                    href={`tel:${cleanTel(contactSettings.mobile)}`}
                    className="text-sm text-white/80 hover:text-[#C9A040] hover:underline transition-colors"
                    dir="ltr"
                    title="اتصال مباشر"
                  >
                    {contactSettings.mobile}
                  </a>
                </li>
              )}
              {contactSettings.whatsapp && (
                <li className="flex items-center gap-3">
                  <MessageCircle className="w-5 h-5 text-[#C9A040] flex-shrink-0" />
                  <a 
                    href={getWhatsappUrl(contactSettings.whatsapp)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-white/80 hover:text-[#C9A040] hover:underline transition-colors"
                    dir="ltr"
                    title="مراسلة عبر واتساب"
                  >
                    {getWhatsappDisplay(contactSettings.whatsapp)}
                  </a>
                </li>
              )}
              {contactSettings.email && (
                <li className="flex items-center gap-3">
                  <Mail className="w-5 h-5 text-[#C9A040] flex-shrink-0" />
                  <a 
                    href={`mailto:${contactSettings.email}`}
                    className="text-sm text-white/80 hover:text-[#C9A040] hover:underline transition-colors"
                    dir="ltr"
                    title="إرسال بريد إلكتروني"
                  >
                    {contactSettings.email}
                  </a>
                </li>
              )}
            </ul>

            {/* Social Media */}
            <div className="mt-6">
              <h4 className="font-semibold text-sm mb-3">تابعنا</h4>
              <div className="flex gap-3 flex-wrap">
                {contactSettings.facebook && (
                  <a href={contactSettings.facebook} target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-white/10 hover:bg-[#C9A040] transition-colors flex items-center justify-center">
                    <Facebook className="w-5 h-5" />
                  </a>
                )}
                {contactSettings.twitter && (
                  <a href={contactSettings.twitter} target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-white/10 hover:bg-[#C9A040] transition-colors flex items-center justify-center">
                    <Twitter className="w-5 h-5" />
                  </a>
                )}
                {contactSettings.instagram && (
                  <a href={contactSettings.instagram} target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-white/10 hover:bg-[#C9A040] transition-colors flex items-center justify-center">
                    <Instagram className="w-5 h-5" />
                  </a>
                )}
                {contactSettings.youtube && (
                  <a href={contactSettings.youtube} target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-white/10 hover:bg-[#C9A040] transition-colors flex items-center justify-center">
                    <Youtube className="w-5 h-5" />
                  </a>
                )}
                {contactSettings.linkedin && (
                  <a href={contactSettings.linkedin} target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-white/10 hover:bg-[#C9A040] transition-colors flex items-center justify-center">
                    <Linkedin className="w-5 h-5" />
                  </a>
                )}
                {contactSettings.telegram && (
                  <a href={contactSettings.telegram} target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-white/10 hover:bg-[#C9A040] transition-colors flex items-center justify-center">
                    <Send className="w-5 h-5" />
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-white/10 mt-12 pt-8 text-center space-y-3">
          <p className="text-sm sm:text-base text-white/90 font-medium">
            جميع الحقوق محفوظة © 2026 جامعة ابن النفيس
          </p>
          <p className="text-sm text-white/80 flex items-center justify-center gap-1.5 flex-wrap">
            <span>تصميم المهندس / هلال العنس :</span>
            <a 
              href="https://wa.me/967774956756" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-[#C9A040] hover:text-[#FFD54F] font-semibold underline underline-offset-4 transition-colors inline-flex items-center gap-1 hover:scale-105"
              dir="ltr"
              title="تواصل عبر واتساب"
            >
              <span>774956756</span>
            </a>
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
